import { css } from "@emotion/react";
import arrayMutators from "final-form-arrays";
import { FC, ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import { Field, FieldInputProps, FieldMetaState, Form, FormSpy } from "react-final-form";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";

import { ArrowBack, LockBlack, People } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  FormField,
  Heading,
  IPickerListItem,
  Icon,
  Input,
  Picker,
  SegmentedControl,
  SpacingToken,
  Text,
  Textarea,
  Tooltip,
} from "@livechat/design-system-react-components";

import { FixedSaveBar, SaveBarMode } from "components/form";
import { ViewContainer } from "components/grid";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { ChangesNotSavedModal } from "components/modals/ChangesNotSavedModal";
import { mapTeamsToPickerItems } from "components/picker-items/teamPickerItems";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { useMacros } from "hooks/use-macros";
import { ActionPickerList } from "routes/automations/shared/actions/ActionPickerList";
import { validateActions } from "routes/automations/shared/validators";
import { OpenedAccordionIndex } from "routes/automations/workflows/interfaces";
import { selectAgentsAlphabetically, selectIsAdmin } from "store/entities/agents";
import { selectCannedResponsesArray } from "store/entities/canned-responses";
import { selectDefaultTeamId, selectMaxPrivateMacrosPerAgent, selectMaxSharedMacros } from "store/entities/license";
import { IMacro, IMacroAction } from "store/entities/macros/interfaces";
import { selectTagsAlphabetically } from "store/entities/tags";
import { selectCurrentAgentTeams, selectTeamsAlphabetically } from "store/entities/teams";
import { getCurrentAgentId } from "store/features/session";
import { textEllipsisStyles } from "styles/common";
import { withVar } from "themes/helpers";

import { AutomationType, AutomationsRoutes } from "../../constants";
import { isPrivateMacro, isSharedMacro, replaceMacroTemplateStrings } from "../helpers";
import { IMacroEditorLocationState } from "../interfaces";

import { MacroLimitReachedBanner } from "./MacroLimitReachedBanner";

export interface IMacroForm {
  shared: boolean;
  teams: {
    key: string;
    name: string;
    customElement: {
      listItemBody: ReactElement;
      selectedItemBody: ReactElement;
    };
  }[];
  name: string;
  description: string;
  actions: (IMacroAction | Record<string, never>)[];
}

interface IProps {
  macro?: IMacro;
  submitLabel: string;
  submitLoading: boolean;
  onSubmit: (values: IMacroForm) => void;
  headerTitle: string;
}

const singleDisabledPickerItem = (name: string): IPickerListItem[] => [
  {
    key: name,
    name,
    customElement: {
      listItemBody: (
        <Flex alignItems="center" gap={withVar(SpacingToken.Spacing2)}>
          <Text css={textEllipsisStyles}>{name}</Text>
        </Flex>
      ),
      selectedItemBody: (
        <Flex alignItems="center" gap={withVar(SpacingToken.Spacing1)}>
          {name}
        </Flex>
      ),
    },
  },
];

const MacroEditor: FC<IProps> = ({ macro, submitLabel, submitLoading, onSubmit, headerTitle }) => {
  const history = useHistory();
  const location = useLocation<IMacroEditorLocationState>();

  const [openedAccordionIndex, setOpenedAccordionIndex] = useState<OpenedAccordionIndex>(null);
  const [formPristine, setFormPristine] = useState(true);
  const [isChangesNotSavedModalOpen, setIsChangesNotSavedModalOpen] = useState(false);

  const actionPickerRefs = useRef<Array<HTMLDivElement | null>>([]);
  const isAdmin = useSelector(selectIsAdmin);
  const currentAgentId = useSelector(getCurrentAgentId);
  const defaultTeamId = useSelector(selectDefaultTeamId);
  const currentAgentTeams = useSelector(selectCurrentAgentTeams);
  const allTeams = useSelector(selectTeamsAlphabetically);
  const allAgents = useSelector(selectAgentsAlphabetically);
  const allTags = useSelector(selectTagsAlphabetically);
  const allCannedResponses = useSelector(selectCannedResponsesArray);
  const { privateMacros, sharedMacros } = useMacros();
  const maxSharedMacros = useSelector(selectMaxSharedMacros);
  const maxPrivateMacrosPerAgent = useSelector(selectMaxPrivateMacrosPerAgent);
  const isInEditMode = location.pathname.includes(AutomationsRoutes.EditMacro);
  const isInDuplicateMode = location.pathname.includes(`${AutomationsRoutes.AddMacro}/${macro?.id}`);
  const macroTemplate = location.state?.macroTemplate
    ? replaceMacroTemplateStrings(location.state.macroTemplate, currentAgentId, defaultTeamId)
    : undefined;
  const disableTeamPicker = macro && macro.teamIds === null;

  const pickerItems = mapTeamsToPickerItems(isAdmin ? allTeams : currentAgentTeams);

  const selectedItems = (input: FieldInputProps<IPickerListItem[]>): IPickerListItem[] => {
    if (disableTeamPicker) {
      return singleDisabledPickerItem("All teams");
    }
    return input.value || [];
  };

  const initialMacroCreateFormValues: IMacroForm = macro
    ? {
        shared: macro.agentId === null,
        teams: mapTeamsToPickerItems(allTeams.filter(({ id }) => macro.teamIds?.includes(id))),
        name: isInEditMode ? macro.name : `Copy of ${macro.name}`,
        description: macro.description,
        actions: macro.actions,
      }
    : {
        shared: macroTemplate?.agentId === null || false,
        teams: mapTeamsToPickerItems(allTeams.filter(({ id }) => macroTemplate?.teamIds?.includes(id))),
        name: macroTemplate?.name || "",
        description: macroTemplate?.description || "",
        actions: macroTemplate?.actions || [],
      };

  const segmentedControl = (input: FieldInputProps<boolean>): ReactElement => (
    <SegmentedControl
      initialId={initialMacroCreateFormValues.shared ? "shared" : "private"}
      buttons={[
        { id: "private", label: "Private", icon: <Icon source={LockBlack} data-testid="private-switch" /> },
        {
          id: "shared",
          label: "Shared",
          icon: <Icon source={People} data-testid="shared-switch" />,
          disabled: !isAdmin,
        },
      ]}
      onButtonClick={id => input.onChange(id === "shared")}
    />
  );

  const picker = (
    input: FieldInputProps<IPickerListItem[]>,
    meta: FieldMetaState<IPickerListItem[]>,
    macrosLimitReached: boolean
  ): ReactNode => (
    <Picker
      {...input}
      type="multi"
      error={(meta.touched || meta.modified) && meta.error}
      placeholder="Select teams"
      selectAllOptionText="Select all"
      selected={selectedItems(input)}
      options={pickerItems}
      onSelect={selectedOptions => input.onChange(selectedOptions)}
      disabled={macrosLimitReached || disableTeamPicker}
      data-testid="teams-picker"
    />
  );

  const handleBackToList = (pristine: boolean): void => {
    if (pristine) {
      history.push(AutomationsRoutes.Macros);
    } else {
      setIsChangesNotSavedModalOpen(true);
    }
  };

  const handleDiscardChanges = (): void => {
    history.push(AutomationsRoutes.Macros);
  };

  const handleBackToEditing = (): void => {
    setIsChangesNotSavedModalOpen(false);
  };

  useEffect(() => {
    if (!isInEditMode) {
      trackEngagement("Macros: Macro creation started", EventPlace.Automations, {
        place: "main",
        premadeType: macroTemplate?.premadeType || "Null",
        role: isAdmin ? "admin" : "agent",
      });
    }
  }, [isInEditMode]);

  return (
    <ViewContainer
      title={macroTemplate ? "New macro from template" : headerTitle}
      contentCss={css({ overflowY: "scroll" })}
      stackShowBackButton
      backButton={
        <Flex marginLeft={withVar(SpacingToken.Spacing3)} alignItems="center">
          <Button
            kind="plain"
            size="compact"
            icon={<Icon source={ArrowBack} />}
            onClick={() => handleBackToList(formPristine)}
          />
        </Flex>
      }
    >
      <Form
        keepDirtyOnReinitialize
        initialValues={initialMacroCreateFormValues}
        onSubmit={onSubmit}
        mutators={{
          ...arrayMutators,
        }}
        validate={(values): { actions: ReturnType<typeof validateActions> } => ({
          actions:
            !values.actions || values.actions?.length === 0
              ? ["At least one action must be added."]
              : validateActions(values.actions, allTeams, allAgents, allCannedResponses, allTags),
        })}
        render={({ handleSubmit, values, invalid, submitFailed, dirtySinceLastSubmit, errors }) => {
          const isSharedMacroLimitReached = sharedMacros.length >= maxSharedMacros;

          const isPrivateMacroLimitReached = privateMacros.length >= maxPrivateMacrosPerAgent;

          const isMacroLimitReached = values.shared ? isSharedMacroLimitReached : isPrivateMacroLimitReached;

          const displayMacroLimitReached = isInEditMode
            ? macro &&
              ((isPrivateMacro(macro) && values.shared && isSharedMacroLimitReached) ||
                (isSharedMacro(macro) && !values.shared && isPrivateMacroLimitReached))
            : isMacroLimitReached;

          const scrollToFirstErrorOnSubmit = (): void => {
            const firstActionWithError = (errors?.actions as string[] | undefined)?.findIndex(
              error => error !== undefined
            );

            if (firstActionWithError !== undefined && actionPickerRefs.current[firstActionWithError]) {
              setTimeout(() => {
                actionPickerRefs.current[firstActionWithError]?.scrollIntoView({ behavior: "smooth", block: "center" });
              }, 0);
            }

            handleSubmit();
          };

          return (
            <Flex padding={withVar(SpacingToken.Spacing8)} justifyContent="center">
              <Flex maxWidth="700px" width="100%" column gap={withVar(SpacingToken.Spacing8)} paddingLeft="50px">
                <form onSubmit={scrollToFirstErrorOnSubmit}>
                  <Flex column>
                    <Flex
                      column
                      alignItems="flex-start"
                      gap={withVar(SpacingToken.Spacing1)}
                      marginBottom={withVar(SpacingToken.Spacing6)}
                    >
                      <Field<IMacroForm["shared"]>
                        name="shared"
                        type="checkbox"
                        render={({ input }) =>
                          isAdmin ? (
                            segmentedControl(input)
                          ) : (
                            <Tooltip
                              placement="bottom-end"
                              arrowOffsetX={50}
                              triggerRenderer={() => segmentedControl(input)}
                              theme="invert"
                              css={{ width: "180px", textAlign: "center" }}
                            >
                              <Text>Only admins can create shared macros</Text>
                            </Tooltip>
                          )
                        }
                      />
                      <Text size="sm" css={{ color: withVar(DesignToken.ContentBasicSecondary) }}>
                        {values.shared
                          ? "All team members will be able to apply this macro to tickets assigned to selected teams"
                          : "Only you'll be able to apply this macro to tickets assigned to selected teams"}
                      </Text>
                    </Flex>
                    {displayMacroLimitReached && (
                      <MacroLimitReachedBanner
                        isAdmin={isAdmin}
                        isSharedMacro={values.shared}
                        maxSharedMacros={maxSharedMacros}
                        maxPrivateMacrosPerAgent={maxPrivateMacrosPerAgent}
                      />
                    )}
                    <Flex column maxWidth="360px" width="100%">
                      <Field<IMacroForm["teams"]>
                        name="teams"
                        multiple
                        validate={value =>
                          disableTeamPicker || (value && value.length) ? undefined : "Select at least one team"
                        }
                        render={({ input, meta }) => (
                          <FormField
                            labelText="Select for which teams this macro will be available"
                            error={(meta.touched || meta.modified) && meta.error}
                          >
                            {disableTeamPicker ? (
                              <Tooltip
                                kind="invert"
                                placement="top"
                                triggerRenderer={() => picker(input, meta, displayMacroLimitReached ?? false)}
                              >
                                <Flex maxWidth="180px" width="100%" css={{ textAlign: "center" }}>
                                  <Text>This cannot be changed because it is set by the API</Text>
                                </Flex>
                              </Tooltip>
                            ) : (
                              picker(input, meta, displayMacroLimitReached ?? false)
                            )}
                          </FormField>
                        )}
                      />
                    </Flex>
                    <Flex column width="100%" maxWidth="900px" gap={withVar(SpacingToken.Spacing2)}>
                      <Heading size="xs" css={{ color: withVar(DesignToken.ContentBasicPrimary) }}>
                        Specify actions
                      </Heading>
                      <ActionPickerList
                        actionPickerRefs={actionPickerRefs}
                        limitReached={displayMacroLimitReached ?? false}
                        openedAccordionIndex={openedAccordionIndex}
                        setOpenedAccordionIndex={setOpenedAccordionIndex}
                        automationType={AutomationType.Macro}
                      />
                    </Flex>
                    <Flex column width="100%" maxWidth="640px" marginBottom={withVar(SpacingToken.Spacing20)}>
                      <Block marginTop={withVar(SpacingToken.Spacing1)} marginBottom={withVar(SpacingToken.Spacing5)}>
                        <Heading size="xs" css={{ color: withVar(DesignToken.ContentBasicPrimary) }}>
                          Describe macro
                        </Heading>
                      </Block>
                      <Field<IMacroForm["name"]>
                        name="name"
                        validate={value => (value ? undefined : "Type the required macro name")}
                        render={({ input, meta }) => (
                          <Flex column>
                            <FormField labelText="Name" error={meta.touched && meta.error}>
                              <Input
                                data-testid="macro-name-input"
                                {...input}
                                placeholder="Type name..."
                                value={input.value}
                                onChange={event => input.onChange(event.target.value)}
                                data-1p-ignore
                                autoComplete="off"
                                error={meta.error && meta.touched}
                                disabled={displayMacroLimitReached}
                              />
                            </FormField>
                          </Flex>
                        )}
                      />
                      <Field
                        name="description"
                        render={({ input }) => (
                          <Flex column width="100%" maxWidth="640px">
                            <FormField labelText="Description">
                              <Textarea
                                placeholder="Type description..."
                                value={input.value}
                                disabled={displayMacroLimitReached}
                                onChange={event => input.onChange(event.target.value)}
                                // TODO: remove styles when api for Textarea will be extended
                                css={css`
                                  width: 100%;
                                  max-width: 640px;
                                  textarea {
                                    resize: none;
                                    width: 100%;
                                    max-width: 640px;
                                    box-sizing: border-box;
                                    height: 76px;
                                  }
                                `}
                              />
                            </FormField>
                          </Flex>
                        )}
                      />
                    </Flex>
                    <FixedSaveBar
                      mode={
                        formPristine && !isInDuplicateMode && !macroTemplate
                          ? SaveBarMode.Hidden
                          : submitLoading
                            ? SaveBarMode.Saving
                            : SaveBarMode.OfferSave
                      }
                      onCancel={() => handleBackToList(formPristine)}
                      onSave={scrollToFirstErrorOnSubmit}
                      customSaveLabel={submitLabel}
                      saveDisabled={displayMacroLimitReached || (invalid && submitFailed && !dirtySinceLastSubmit)}
                    />
                  </Flex>
                </form>
              </Flex>
              <FormSpy subscription={{ pristine: true }} onChange={({ pristine }) => setFormPristine(pristine)} />
            </Flex>
          );
        }}
      />
      <ChangesNotSavedModal
        isOpen={isChangesNotSavedModalOpen}
        onDiscardChanges={handleDiscardChanges}
        onBackToEditing={handleBackToEditing}
      >
        {"You have unsaved changes in the macro. They'll be lost if you close the macro editor without saving them."}
      </ChangesNotSavedModal>
    </ViewContainer>
  );
};

export default MacroEditor;
